import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injector } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BusinessRuleError, IKeyValue } from '@carm/_utils/models';
import { TranslateService } from '@ngx-translate/core';
import { IErrorResponse, IMappedError } from '../_utils/models/error.model';
import { NotificationService } from './notification-mgt/facade/notifcation.service';
import { UserService } from '@carm/_core/access-control-mgt/facade/user.service';
import { LoggerService } from './logger/logger.service';
import { CarmBlob } from './attachments/models/carm-blob.model';
import { CarmRouter } from './carm-router/services/carm-router.service';
import { LoaderSpinnerTypes } from './models/loader-types.model';
import { loaderSpinnerContext } from '@carm/shared/constants/const-carm';
import { AuthService } from '@carm/_auth/spartacus/core/auth';
import { RoutesConst } from '@carm/shared/constants/const-routes';

/**
 * Base api service: To Handle all http related calls
 */
export abstract class BaseApiService {
  private readonly numberOfTries: number = 4; // Number of tries
  private readonly delayInRetry: number = 2000; // Number of miliseconds between each retry

  private getTimeout = 45000; // Total number of miliseconds to throw timeout error (for GET requests)
  private readonly skipRetryOnStatuses: number[] = [500, 402, 401];
  private token: string;
  private isLoggedIn: boolean;
  protected http: HttpClient;
  protected authService: AuthService;
  protected notificationService: NotificationService;
  protected translateService: TranslateService;
  protected loggerService: LoggerService;
  public userService: UserService;
  protected carmRouter: CarmRouter;
  protected get endPointBP(): string {
    return `${environment.baseApiUrls}/business/${this.carmRouter.bn9}/program/${this.carmRouter.bn15}`
  }
  protected get endPointB(): string {
    return `${environment.baseApiUrls}/business/${this.carmRouter.bn9}`
  }
  protected get endPointNB(): string {
    return `${environment.baseApiUrls}/business/${this.carmRouter.nativeBn9}`
  }

  protected programId(): string {
    return this.carmRouter.bn15
  }

  constructor(protected injector: Injector) {
    // Load dependencies using Injector so that we can decouple BaseApiService extending services
    this.http = this.injector.get(HttpClient);
    this.authService = this.injector.get(AuthService);
    this.notificationService = this.injector.get(NotificationService);
    this.translateService = this.injector.get(TranslateService);
    this.loggerService = this.injector.get(LoggerService);
    this.carmRouter = this.injector.get(CarmRouter);

    this.numberOfTries = environment.httpConfig.numberOfTries;
    this.delayInRetry = environment.httpConfig.delayInRetry;
    this.getTimeout = environment.httpConfig.getTimeout;

    this.authService
      .getUserToken()
      .subscribe(userToken =>
        userToken ? (this.token = userToken.access_token) : undefined
      );
    this.authService
      .isUserLoggedIn()
      .subscribe(isLoggedIn => (this.isLoggedIn = isLoggedIn));
    this.userService = this.injector.get(UserService);
  }

  /**
   * Http get
   * @param URL Url of endpoint
   * @returns observable of stream
   */
  protected httpGet<T extends IErrorResponse>(
    URL: string,
    httpParams: HttpParams = null,
    loaderSpinnerType = LoaderSpinnerTypes.FullPage
  ): Observable<T> {

    const extraContext = new HttpContext();
    extraContext.set(loaderSpinnerContext, loaderSpinnerType)

    return this.http
      .get<T>(URL, {
        responseType: 'json',
        params: httpParams,
        headers: this.getRequestHeaders(),
        context: extraContext
      })
      .pipe(
        // retryWhen(this.retryStrategy()),
        timeout(this.getTimeout),
        catchError(err => this.handleHttpError(err)),
        tap(response => this.handleBusinessRuleError(response))
      );
  }

  /**
   * Http post data to server
   * @param URL Url of endpoint
   * @param data Data to post on server
   * @param [errorHandling] optional errorHandling callback
   * @returns post
   */
  protected httpPost<T extends IErrorResponse>(
    URL: string,
    data: any,
    errorHandling: boolean = true
  ): Observable<T> {
    if (errorHandling) {
      return this.http
        .post<T>(URL, data, {
          responseType: 'json',
          headers: this.getRequestHeaders(),
        })
        .pipe(
          catchError(err => this.handleHttpError(err)),
          tap(response => this.handleBusinessRuleError(response))
        );
    } else {
      return this.http.post<T>(URL, data, {
        responseType: 'json',
        headers: this.getRequestHeaders(),
      });
    }
  }

  /**
   * HttGet data from server using auth token
   * @param URL Url of endpoint
   * @param [requestOptions] optional request paramerters
   * @returns observable of stream
   */
  protected httpAuthGet<T extends IErrorResponse>(
    URL: string,
    httpParams: HttpParams = null,
    loaderSpinnerType = LoaderSpinnerTypes.FullPage
  ): Observable<T> {
    // Check if user is logged in
    if (!this.isLoggedIn) {
      return throwError('You are not logged in!');
    }

    const extraContext = new HttpContext();
    extraContext.set(loaderSpinnerContext, loaderSpinnerType)

    // generic httpget must use JSON responseType. create a new httpAuthGet if ever blob, array buffer is used.
    return this.http
      .get<T>(URL, {
        responseType: 'json',
        params: httpParams,
        headers: this.getRequestHeaders(),
        context: extraContext
      })
      .pipe(
        // retryWhen(this.retryStrategy()),
        timeout(this.getTimeout),
        catchError(err => this.handleHttpError(err)),
        tap(response => this.handleBusinessRuleError(response))
      );
  }

  /**
   * HttGet filestream from server using auth token
   * @param URL Url of endpoint
   * @returns observable of Blob
   */
  protected httpAuthDownloadFile(
    URL: string,
    httpParams: HttpParams = null
  ): Observable<CarmBlob> {
    // Check if user is logged in
    if (!this.isLoggedIn) {
      throwError('You are not logged in!');
    }

    const headers = new HttpHeaders().set(
      'authorization',
      `Bearer ${this.token}`
    );
    return this.http.get(URL, {
      headers,
      params: httpParams,
      responseType: 'blob',
    })
    .pipe(
      timeout(this.getTimeout),
      catchError(err => this.handleBlobError(err))
    );
  }

  // Send data to server using auth token
  protected httpAuthPost<T extends IErrorResponse>(
    URL: string,
    payload: any,
    httpParams?: HttpParams,
    loaderSpinnerType = LoaderSpinnerTypes.FullPage
  ): Observable<T> {

    const extraContext = new HttpContext();
    extraContext.set(loaderSpinnerContext, loaderSpinnerType)

    return this.http
      .post<T>(URL, payload, { params: httpParams, headers: this.getRequestHeaders(), context: extraContext })
      .pipe(
        timeout(this.getTimeout),
        catchError(err => this.handleHttpError(err)),
        tap(response => this.handleBusinessRuleError(response))
      );
  }

  protected httpAuthPatch<T extends IErrorResponse>(
    URL: string,
    data: any
  ): Observable<T> {

    return this.http
      .patch<T>(URL, data, { headers: this.getRequestHeaders() })
      .pipe(
        catchError(err => this.handleHttpError(err)),
        tap(response => this.handleBusinessRuleError(response))
      );
  }

  // Send data to server using auth token
  protected httpAuthPut<T extends IErrorResponse>(
    URL: string,
    data: any
  ): Observable<T> {

    return this.http
      .put<T>(URL, data, { headers: this.getRequestHeaders() })
      .pipe(
        timeout(this.getTimeout),
        catchError(err => this.handleHttpError(err)),
        tap(response => this.handleBusinessRuleError(response))
      );
  }

  protected httpAuthDelete<T extends IErrorResponse>(
    URL: string,
    httpParams: HttpParams = null,
    data: any = null
  ): Observable<T> {

    return this.http
      .request<T>('delete', URL, {
        headers: this.getRequestHeaders(),
        params: httpParams,
        body: data,
      })
      .pipe(
        timeout(this.getTimeout),
        catchError(err => this.handleHttpError(err)),
        tap(response => this.handleBusinessRuleError(response))
      );
  }

  protected getRequestHeaders(): HttpHeaders {
    let headers = new HttpHeaders(); // HttpHeaders is immutable - every set(), append(), delete() returns a new clone
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Content-Type', 'application/json');

    if (this.isLoggedIn) {
      headers = headers.append('authorization', `Bearer ${this.token}`);
    }

    return headers;
  }

  protected getCarmToken() {
    return this.token;
  }

  /**
   * Handles error
   * @param error ErroObject
   * @returns error Map http error on errorModel
   */
  protected handleHttpError(httpErrorResponse: HttpErrorResponse): Observable<never> {
    try {
      this.loggerService.logHttpError(httpErrorResponse);
    } catch (error) {
      return throwError(error);
    }

    // BE specifically uses these statusCodes to return custom errors
    if (
      httpErrorResponse.status === 422 ||
      httpErrorResponse.status === 400
    ) {
      console.error('HttpError: ', httpErrorResponse.error);

      const errorResponse = httpErrorResponse.error as IErrorResponse;

      this.verifyDeploymentInProgressError(errorResponse);

      this.handleBusinessRuleError(errorResponse);
    } else {
      return throwError(httpErrorResponse);
    }
  }

  protected handleBlobError(
    httpErrorResponse: HttpErrorResponse
  ): Observable<never> {

    if (
      httpErrorResponse.status === 422 ||
      httpErrorResponse.status === 400
    ) {
      console.error('HttpError: ', httpErrorResponse.error);

      if(!(httpErrorResponse.error instanceof Blob)){
        const errorResponse = httpErrorResponse.error as IErrorResponse;

        this.verifyDeploymentInProgressError(errorResponse);
        
        this.handleBusinessRuleError(errorResponse);
      }else{

        const errorCode ='carm.exception.opentext.document.download.failure';
        const documentDownloadError: IMappedError = { key: 'document_download', value: { errors: [ { code: errorCode, message: errorCode}] } }

        throw new BusinessRuleError([documentDownloadError]);
      }
    } else {
      return throwError(httpErrorResponse);
    }
  }

  protected handleBusinessRuleError<T extends IErrorResponse>(
    data: T
  ): Observable<T> {
    const localizedErrors: IKeyValue[] = [];
    const ngxTranslatePrefix = 'errors.';
    const ngxTranslateFallback = 'unknown';

    if (data && data.mappedErrorList && data.mappedErrorList.length > 0) {
      console.error('Error(s): (mappedErrorList)', data.mappedErrorList);

      data.mappedErrorList.forEach(emv => {
        emv.value.errors.forEach(err => {
          let message: IKeyValue;

          if (
            this.translateService.instant(ngxTranslatePrefix + err.code) ===
            ngxTranslatePrefix + err.code
          ) {
            message = {
              key: err.code,
              value: `${this.translateService.instant(
                ngxTranslatePrefix + ngxTranslateFallback
              )} (${err.code})`,
            };
          } else {
            message = {
              key: err.code,
              value: this.translateService.instant(
                ngxTranslatePrefix + err.code
              ),
            };
          }

          if (err.message) {
            console.error(err.message);
            message = { key: err.code, value: err.message };
          }
          localizedErrors.push(message);
        });
      });

      throw new BusinessRuleError(data.mappedErrorList);
    }

    return of(data);
  }

  private verifyDeploymentInProgressError(errorResponse: IErrorResponse){
    if (errorResponse && errorResponse.mappedErrorList
      && errorResponse.mappedErrorList[0].key == 'DEPLOYMENT_IN_PROGRESS'){
      this.authService.logout();
      this.userService.logOut();
      this.carmRouter.carmNavigate([RoutesConst.Logout], {queryParams: { DEPLOYMENT_IN_PROGRES: true}});
      return;
    }
  }
}
